// BrazilV2Tab.tsx
import React, { useState } from "react";
import AstriaBrazilV2Shell, {
  SHELL_COLORS_BRAZIL_V2,
  AstriaBrazilV2Tab as TabKey,
  ASTRIA_BRAZIL_V2_TABS,
} from "./AstriaBrazilV2Shell";
import {
  AstriaIndiaV2ResultCard,
  IndiaV2LoadingState,
  IndiaV2ErrorState,
  IndiaV2ChatCta,
} from "../indiaV2/AstriaIndiaV2Card";
import {
  WizardChips,
  WizardSlider,
  WizardEmojiWheel,
  WizardTextInput,
} from "../indiaV2/AstriaIndiaV2Wizard";
import { useBrazilV2Tab, BrazilV2Wizard } from "./useBrazilV2Tab";
import { BRAZIL_V2_TAB_CONFIGS } from "./brazilV2TabConfig";
import { useBrazilV2Language } from "./brazilV2Language";
import { BRAZIL_V2_COPY } from "./brazilV2Copy";

// ─── One generic single-question tab renderer, parameterized by `tabKey` ───
// All 6 Astria Brazil V2 tabs share the same shape (per client spec —
// BrMaxico.txt BR v2 UI pack: one screen, 1-2 controls, submit → reveal a
// copy-pack line) so a single component + a per-tab config
// (brazilV2TabConfig.ts) replaces what would otherwise be 6 nearly
// duplicate files. Result rendering reuses AstriaIndiaV2ResultCard directly
// (country-agnostic) since the response here is always plain text — no
// structured fields to break out, unlike India V2's subcategories.
//
// Bilingual (EN/PT): displayed question/helper/field/chip text is sourced
// from brazilV2Copy.ts, keyed by the active brazilV2Language.ts toggle. The
// same toggle is sent to the backend as `wizard.lang`, which picks the
// matching EN/PT copy pack there too (see
// helper/astriaBrazilV2Service.js's EN_COPY_PACK/BR_COPY_PACK) — so the
// check-in result text (`message.content`) matches the selected language,
// not just the surrounding UI chrome.
//
// Same pattern as VietnamV2Tab.tsx / PhilippinesV2Tab.tsx / IndonesiaV2Tab.tsx.

interface BrazilV2TabProps {
  tabKey: TabKey;
}

const BrazilV2Tab: React.FC<BrazilV2TabProps> = ({ tabKey }) => {
  const [lang, setLang] = useBrazilV2Language();
  const copy = BRAZIL_V2_COPY[lang];
  const screenCopy = copy.screens[tabKey];
  const tabBarCopy = copy.tabBar[tabKey];

  const config = BRAZIL_V2_TAB_CONFIGS.find((c) => c.tab === tabKey)!;
  const tabDef = ASTRIA_BRAZIL_V2_TABS.find((t) => t.key === tabKey)!;
  const { loading, error, message, send, reset, goToChat } = useBrazilV2Tab();

  const [values, setValues] = useState<Record<string, string>>({});

  const started = !!message || loading || !!error;

  const setField = (key: string, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const isValid = config.fields.every((f) => {
    if (f.type === "slider") return true; // sliders always have a value
    return !!values[f.key];
  });

  const handleSubmit = () => {
    const wizard: BrazilV2Wizard = { tab: tabKey, ...values, lang };
    // Always resolve against the DB-seeded English subcategory name, not
    // the display label — the display label changes with `lang` but the
    // backend SubCategory.name does not (see AstriaBrazilV2Shell's
    // TabDef.dbName doc comment).
    send(tabDef.dbName, wizard);
  };

  if (started) {
    return (
      <AstriaBrazilV2Shell active={tabKey} lang={lang} onLangChange={setLang}>
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: SHELL_COLORS_BRAZIL_V2.titleColor,
              marginBottom: 4,
            }}
          >
            {tabBarCopy.label}
          </div>
          <p style={{ fontSize: 13.5, color: SHELL_COLORS_BRAZIL_V2.mutedColor }}>
            {tabBarCopy.sublabel}
          </p>
        </div>

        {loading && <IndiaV2LoadingState label={copy.loadingLabel} />}

        {!loading && error && (
          <IndiaV2ErrorState message={error} onRetry={handleSubmit} />
        )}

        {!loading && !error && message && (
          <>
            <AstriaIndiaV2ResultCard
              icon={<span style={{ fontSize: 20 }}>{config.icon}</span>}
              title={tabBarCopy.label}
              subtitle={tabBarCopy.sublabel}
              accent={tabDef.accent}
              shareText={message.content}
            >
              <p
                style={{
                  fontSize: 15,
                  lineHeight: 1.75,
                  color: SHELL_COLORS_BRAZIL_V2.bodyColor,
                  whiteSpace: "pre-wrap",
                }}
              >
                {message.content}
              </p>
            </AstriaIndiaV2ResultCard>
            <button
              onClick={() => {
                reset();
                setValues({});
              }}
              className="text-xs font-semibold transition-colors"
              style={{ marginTop: 14, color: SHELL_COLORS_BRAZIL_V2.mutedColor }}
            >
              {copy.resetLink}
            </button>
            <IndiaV2ChatCta onClick={goToChat} />
          </>
        )}
      </AstriaBrazilV2Shell>
    );
  }

  return (
    <AstriaBrazilV2Shell active={tabKey} lang={lang} onLangChange={setLang}>
      <h2
        style={{
          fontSize: 21,
          fontWeight: 700,
          color: SHELL_COLORS_BRAZIL_V2.titleColor,
          marginBottom: screenCopy.helper ? 6 : 20,
          lineHeight: 1.35,
        }}
      >
        {screenCopy.question}
      </h2>
      {screenCopy.helper && (
        <p
          style={{
            fontSize: 13.5,
            color: SHELL_COLORS_BRAZIL_V2.mutedColor,
            marginBottom: 20,
            lineHeight: 1.6,
          }}
        >
          {screenCopy.helper}
        </p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {config.fields.map((field) => {
          const fieldCopy = screenCopy.fields[field.key];
          const options = field.optionValues?.map((value) => ({
            value,
            label: fieldCopy.options?.[value] || value,
          }));

          return (
            <div key={field.key}>
              {field.type !== "emoji_wheel" && (
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: SHELL_COLORS_BRAZIL_V2.mutedColor,
                    marginBottom: 8,
                  }}
                >
                  {fieldCopy.label}
                </div>
              )}

              {field.type === "emoji_wheel" && (
                <WizardEmojiWheel
                  value={values[field.key] || null}
                  onChange={(v) => setField(field.key, v)}
                  colors={SHELL_COLORS_BRAZIL_V2}
                />
              )}
              {field.type === "slider" && (
                <WizardSlider
                  value={Number(values[field.key] ?? 50)}
                  onChange={(v) => setField(field.key, String(v))}
                  lowLabel={fieldCopy.lowLabel}
                  highLabel={fieldCopy.highLabel}
                  colors={SHELL_COLORS_BRAZIL_V2}
                />
              )}
              {field.type === "chips" && options && (
                <WizardChips
                  options={options}
                  value={values[field.key] || null}
                  onChange={(v) => setField(field.key, v)}
                  colors={SHELL_COLORS_BRAZIL_V2}
                />
              )}
              {field.type === "text" && (
                <WizardTextInput
                  value={values[field.key] || ""}
                  onChange={(v) => setField(field.key, v)}
                  placeholder={fieldCopy.placeholder}
                  multiline
                  colors={SHELL_COLORS_BRAZIL_V2}
                />
              )}
            </div>
          );
        })}
      </div>

      <button
        onClick={handleSubmit}
        disabled={!isValid}
        className="flex items-center justify-center gap-1.5 rounded-full text-sm font-semibold text-white transition-all hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100"
        style={{
          marginTop: 28,
          width: "100%",
          paddingBlock: 14,
          background: `linear-gradient(135deg, ${SHELL_COLORS_BRAZIL_V2.accent} 0%, ${SHELL_COLORS_BRAZIL_V2.gold} 100%)`,
          boxShadow: `0 6px 18px ${SHELL_COLORS_BRAZIL_V2.accentSoft}`,
        }}
      >
        {copy.submitButton}
      </button>
    </AstriaBrazilV2Shell>
  );
};

export default BrazilV2Tab;
